import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { v3 } from '../lib/styleTokens';
import { Logo, Button } from '../components/v3';

export default function LoginPage() {
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  // v3.7.2: default to the bundled company logo. The login page is
  // unauthenticated and frequently can't read pricing_settings, so the default
  // must be the real logo (not the Pulse mark) — Shaun keeps the uploaded logo.
  // The Pulse-mark <Logo> hero only shows if logoUrl is ever cleared.
  const [logoUrl, setLogoUrl] = useState('/ferrum-logo.png');

  useEffect(() => {
    // Try to load custom logo from DB — falls back to the bundled logo if unauthenticated or not set
    supabase.from('pricing_settings').select('value').eq('key', 'company_logo_url').single()
      .then(({ data }) => { if (data?.value) setLogoUrl(data.value); })
      .catch(() => {}); // silently fall back to default
  }, []);
  const { user }  = useAuth();
  const navigate  = useNavigate();

  // Handle errors redirected back from Microsoft/Supabase
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.replace('#', ''));
      const desc = params.get('error_description') || 'Authentication error';
      setError(desc.replace(/\+/g, ' '));
    }
  }, []);

  // Already logged in — go to app
  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  async function signInWithMicrosoft() {
    setError(''); setLoading(true);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile openid',
        redirectTo: window.location.origin + '/',
      }
    });
    if (err) { setError(err.message); setLoading(false); }
    // On success Supabase redirects the browser — no need to handle here
  }

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:v3.color.bg, fontFamily:v3.font.family }}>
      <div style={{ width:400, padding:36, background:v3.color.card, borderRadius:v3.radius.cardLarge, border:`1px solid ${v3.color.border}`, boxShadow:v3.shadow.modal }}>

        {/* Logo / Pulse-mark hero */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, marginBottom:28 }}>
          {logoUrl ? (
            // Custom uploaded logo (transparent PNG) sits directly on the white card.
            <img src={logoUrl} alt="Company Logo"
              style={{ height: 64, maxWidth: 260, objectFit: 'contain', display: 'block' }} />
          ) : (
            <>
              <Logo size={44} />
              <div style={{ fontSize:v3.font.size.label, color:v3.color.muted }}>Intelligence Platform</div>
            </>
          )}
        </div>

        <h2 style={{ fontSize:v3.font.size.header, fontWeight:v3.font.weight.semibold, color:v3.color.body, margin:'0 0 6px', textAlign:'center', letterSpacing:v3.font.letterSpacing.tight }}>Welcome back</h2>
        <p style={{ fontSize:v3.font.size.label, color:v3.color.muted, margin:'0 0 28px', textAlign:'center' }}>
          Sign in with your FerrumIT Microsoft account
        </p>

        {/* Error */}
        {error && (
          <div style={{ padding:'8px 12px', background:v3.color.status.redBg, border:`1px solid ${v3.color.status.redText}22`, borderRadius:v3.radius.input, fontSize:v3.font.size.label, color:v3.color.status.redText, marginBottom:16, textAlign:'center' }}>
            {error}
          </div>
        )}

        {/* Microsoft Sign In Button — single cobalt primary action (binding §1) */}
        <Button
          variant="primary"
          size="lg"
          onClick={signInWithMicrosoft}
          disabled={loading}
          icon={!loading && (
            <svg width="18" height="18" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
          )}
          style={{ width:'100%', justifyContent:'center', gap:10 }}
        >
          {loading ? 'Redirecting to Microsoft...' : 'Sign in with Microsoft'}
        </Button>

        <p style={{ fontSize:v3.font.size.micro, color:v3.color.mutedSoft, textAlign:'center', marginTop:20, lineHeight:1.5 }}>
          Access is restricted to FerrumIT team members.<br/>
          Contact your admin if you need access.
        </p>

        {/* Legal warning banner */}
        <div style={{ marginTop:16, padding:'10px 12px', background:v3.color.status.redBg, border:`1px solid ${v3.color.status.redText}22`, borderRadius:v3.radius.input }}>
          <div style={{ fontSize:v3.font.size.micro, fontWeight:v3.font.weight.bold, color:v3.color.status.redText, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>
            System Use Notice
          </div>
          <p style={{ fontSize:v3.font.size.micro, color:'#7f1d1d', lineHeight:1.6, margin:0 }}>
            Unauthorized or improper use of this system may result in disciplinary action and/or civil charges/criminal penalties and/or prosecution by law. Evidence of unauthorized use collected during monitoring may be used for administrative, criminal, or other adverse action. By continued use of this system, you indicate your awareness of and consent to these terms and conditions of use.
          </p>
        </div>

      </div>
    </div>
  );
}
